import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Trophy, LogOut, FileText, Upload, Loader2, Info, MapPin, Clock, ExternalLink, Award, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, absUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { STATUS, brl, fmtDate, maskCpf, apiError, SPORT_LABEL } from "@/lib/format";
import ShareCard from "@/components/ShareCard";
import RefundRequest from "@/components/RefundRequest";
import { StatusTimeline, AthleteQr } from "@/components/AthleteStatus";

export default function AthletePortal() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [uploading, setUploading] = useState(null);
  const [event, setEvent] = useState(null);
  const [qrUrls, setQrUrls] = useState({});

  const load = () => api.get("/me/registrations").then((r) => {
    setData(r.data);
    r.data.registrations.forEach((reg) => {
      if (reg.status === "PAID" && !qrUrls[reg.id]) {
        api.get(`/me/registrations/${reg.id}/qr`, { responseType: "blob" }).then((q) => setQrUrls((u) => ({ ...u, [reg.id]: URL.createObjectURL(q.data) }))).catch(() => {});
      }
    });
  }).catch(() => {});
  useEffect(() => { load(); api.get("/public/event").then((r) => setEvent(r.data.event)).catch(() => {}); }, []);

  const doLogout = () => { logout(); navigate("/"); };

  const openPdf = async (path, okMsg) => {
    try {
      const res = await api.get(path, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
      toast.success(okMsg);
    } catch (e) { toast.error(apiError(e)); }
  };
  const openReceipt = (reg) => openPdf(`/registrations/${reg.id}/receipt`, "Comprovante gerado!");
  const openCertificate = (reg) => openPdf(`/registrations/${reg.id}/certificate`, "Certificado gerado!");

  const uploadProof = async (reg, file) => {
    if (!file) return;
    setUploading(reg.id);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/registrations/${reg.id}/proof`, fd);
      toast.success("Comprovante enviado! Aguarde validação do Staff.");
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setUploading(null); }
  };

  const runner = data?.runner;
  const regs = data?.registrations || [];

  return (
    <div className="min-h-screen bg-ink text-white">
      <header className="glass border-b border-white/10 sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-4 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center p-1"><img src="/logo-cemoc.png" alt="CEMOC RUN" className="w-full h-full object-contain" /></div>
            <span className="font-display text-xl font-black uppercase">Portal do Atleta</span>
          </div>
          <Button data-testid="logout-btn" variant="ghost" onClick={doLogout} className="text-slate-300 hover:bg-white/5">
            <LogOut className="w-4 h-4 mr-1" /> Sair
          </Button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-8 py-8 space-y-8">
        <div className="animate-fade-up rounded-2xl bg-surface border border-white/10 p-6" data-testid="athlete-personal-data">
          <p className="text-brand font-bold uppercase tracking-widest text-xs flex items-center gap-1.5"><User className="w-3.5 h-3.5" /> Dados do Atleta</p>
          <h1 className="font-display text-3xl sm:text-4xl font-black uppercase mt-1">{user?.name}</h1>
          {runner && (
            <dl className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4 text-sm">
              {[["CPF", maskCpf(runner.cpf)], ["Nascimento", fmtDate(runner.birth_date)], ["Sexo", runner.gender === "F" ? "Feminino" : "Masculino"], ["WhatsApp", runner.whatsapp || "—"]].map(([k, v]) => (
                <div key={k}><dt className="text-[10px] uppercase tracking-widest text-slate-500">{k}</dt><dd className="font-semibold text-slate-200">{v}</dd></div>
              ))}
            </dl>
          )}
        </div>

        <h2 className="font-display text-2xl font-black uppercase flex items-center gap-2" data-testid="my-races-title"><Trophy className="w-6 h-6 text-brand" /> Minhas Corridas</h2>

        <div className="grid grid-cols-1 gap-5">
          {regs.length === 0 && (
            <div className="rounded-2xl bg-surface border border-white/10 p-8 text-center text-slate-400">
              Você ainda não possui inscrições.
            </div>
          )}
          {regs.map((reg) => {
            const st = STATUS[reg.status] || {};
            return (
              <div key={reg.id} data-testid={`reg-card-${reg.bib_code}`}
                className="rounded-2xl bg-surface border border-white/10 overflow-hidden animate-fade-up">
                <div className="p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-white/10">
                  <div className="flex items-center gap-5">
                    {reg.status === "PAID" && <AthleteQr reg={{ ...reg, runner }} qrUrl={qrUrls[reg.id]} />}
                    <div>
                      <div className="text-xs text-slate-400 uppercase tracking-widest mb-1">Código de Inscrição / Nº de Peito</div>
                      <div className="font-mono font-bold text-3xl text-neon tracking-widest" data-testid={`bib-${reg.bib_code}`}>{reg.bib_code}</div>
                      <div className="text-sm text-slate-300 mt-1">{SPORT_LABEL[reg.category?.sport_type]} — {reg.category?.name}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <span data-testid={`reg-status-${reg.bib_code}`} className={`inline-block text-xs font-bold px-3 py-1.5 rounded-full border ${st.bg}`}>
                      {st.label}
                    </span>
                    <div className="font-mono font-bold text-white mt-2">{reg.courtesy ? "Cortesia" : brl(reg.category?.price)}</div>
                  </div>
                </div>

                {reg.timeline && <div className="px-6 pt-6"><StatusTimeline steps={reg.timeline} result={reg.result} /></div>}

                <div className="p-6 flex flex-wrap gap-3">
                  {reg.certificate_available && (
                    <Button data-testid={`certificate-btn-${reg.bib_code}`} onClick={() => openCertificate(reg)}
                      className="bg-white text-ink hover:bg-slate-200 font-bold uppercase tracking-wide">
                      <Award className="w-4 h-4 mr-2" /> Certificado de Participação
                    </Button>
                  )}
                  {(reg.status === "PAID") && (
                    <Button data-testid={`receipt-btn-${reg.bib_code}`} onClick={() => openReceipt(reg)}
                      className="bg-brand hover:bg-brand-hover font-bold uppercase tracking-wide">
                      <FileText className="w-4 h-4 mr-2" /> Emitir Comprovante PDF
                    </Button>
                  )}
                  {(reg.status === "PRE_REGISTERED" || reg.status === "PENDING_PAYMENT") && (
                    <>
                      {reg.payment_link ? (
                        <a data-testid={`pay-link-btn-${reg.bib_code}`} href={reg.payment_link} target="_blank" rel="noopener noreferrer"
                          className="inline-flex items-center px-4 h-10 rounded-md bg-brand hover:bg-brand-hover text-sm font-bold uppercase tracking-wide">
                          <ExternalLink className="w-4 h-4 mr-2" /> Pagar {brl(reg.amount)} (Nubank)
                        </a>
                      ) : (
                        <span data-testid={`pay-link-missing-${reg.bib_code}`} className="inline-flex items-center text-xs text-amber-300 self-center">Link de pagamento indisponível — fale com a organização</span>
                      )}
                      <label className="inline-flex">
                        <input type="file" accept="image/*,application/pdf" className="hidden"
                          data-testid={`proof-input-${reg.bib_code}`}
                          onChange={(e) => uploadProof(reg, e.target.files[0])} />
                        <span className="inline-flex items-center px-4 h-10 rounded-md bg-white/5 border border-white/10 hover:bg-white/10 cursor-pointer text-sm font-bold uppercase tracking-wide">
                          {uploading === reg.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Upload className="w-4 h-4 mr-2" /> Enviar Comprovante</>}
                        </span>
                      </label>
                    </>
                  )}
                  {reg.payment_proof_view_url && (
                    <span className="inline-flex items-center text-xs text-emerald-300 self-center">✓ Comprovante enviado</span>
                  )}
                  <ShareCard reg={reg} runner={runner} event={event} />
                  <RefundRequest reg={reg} onDone={load} />
                </div>
              </div>
            );
          })}
        </div>

        {/* Info geral */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {[{ i: Clock, t: "Cronograma", k: "schedule" }, { i: MapPin, t: "Retirada de Kits", k: "kit_info" }, { i: Info, t: "Percurso", k: "route_info" }].map((b) => (
            <InfoCard key={b.k} icon={b.i} title={b.t} field={b.k} />
          ))}
        </div>
      </main>
    </div>
  );
}

function InfoCard({ icon: Icon, title, field }) {
  const [val, setVal] = useState("");
  useEffect(() => { api.get("/public/event").then((r) => setVal(r.data.event[field] || "")).catch(() => {}); }, [field]);
  return (
    <div className="rounded-2xl bg-surface border border-white/10 p-5">
      <Icon className="w-5 h-5 text-brand mb-2" />
      <h4 className="font-display text-lg uppercase font-bold mb-1">{title}</h4>
      <p className="text-sm text-slate-400 leading-relaxed">{val}</p>
    </div>
  );
}
